import React, { HTMLAttributes, ReactNode } from 'react';

/**
 * StatCard — KPI card with colored left-border accent.
 *
 * Derived from M3 Card (Outlined variant) per Agent Protocol step 2:
 *   - Spec:      https://m3.material.io/components/cards/overview
 *   - Reference: github.com/material-components/material-web (no first-party Card at time
 *                of authoring; consult M3 spec)
 *
 * Populus adaptations applied (per Populus/doctrine/design-system.md):
 *   - KPI composition: small UPPERCASE label + large display number + small descriptive caption
 *   - Colored left-border accent per Status semantic (hunter/amber/green/red/info/neutral)
 *   - Compact density: tighter padding (sp-4) than M3 default (sp-6)
 *
 * Phase 11 Block 6 PR 3a (2026-05-12).
 */

export type StatCardAccent = 'hunter' | 'amber' | 'green' | 'red' | 'info' | 'neutral';

export interface StatCardProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
  value: ReactNode;
  caption?: ReactNode;
  accent?: StatCardAccent;
}

const accentColor: Record<StatCardAccent, string> = {
  hunter: 'var(--brand-primary)',  // brand accent — follows org branding
  amber: 'var(--color-status-amber)',  // semantic — fixed
  green: 'var(--color-status-green)',  // semantic — fixed
  red: 'var(--color-status-red)',  // semantic — fixed
  info: 'var(--color-status-celadon)',  // semantic — fixed
  neutral: 'var(--color-border-strong)',  // structural — fixed
};

export function StatCard({ label, value, caption, accent = 'hunter', style, ...rest }: StatCardProps) {
  // A card with an onClick is an interactive control, so it has to behave like
  // one: reachable by Tab, activated by Enter/Space, and announced as a button.
  // A bare div with a click handler looks identical to a mouse and is invisible
  // to a keyboard or a screen reader.
  const interactive = typeof rest.onClick === 'function';
  const a11y = interactive
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            (rest.onClick as unknown as (ev: unknown) => void)(e);
          }
        },
      }
    : {};
  return (
    <div
      // Feedback only when there is something to click. A hover lift on an inert
      // card is a promise the card does not keep.
      className={[interactive ? 'ig-interactive' : '', rest.className].filter(Boolean).join(' ') || undefined}
      style={{
        background: 'var(--color-card)',
        border: `1px solid var(--color-border)`,
        borderLeft: `3px solid ${accentColor[accent]}`,
        borderRadius: 'var(--r-lg)',
        padding: 'var(--sp-4)',
        boxShadow: 'var(--sh-sm)',
        ...style,
      }}
      {...a11y}
      {...rest}
    >
      <div style={{
        fontSize: 'var(--fs-sm)',
        color: 'var(--color-ink-3)',
        fontWeight: 'var(--fw-medium)',
        marginBottom: 'var(--sp-2)',
      }}>{label}</div>
      <div style={{
        fontSize: 'var(--fs-3xl)',
        fontWeight: 'var(--fw-semibold)',
        color: 'var(--color-ink)',
        letterSpacing: 'var(--ls-tight)',
        lineHeight: 'var(--lh-tight)',
      }}>{value}</div>
      {caption && (
        <div style={{
          fontSize: 'var(--fs-sm)',
          color: 'var(--color-ink-3)',
          marginTop: 'var(--sp-2)',
        }}>{caption}</div>
      )}
    </div>
  );
}

/**
 * StatCardRow — horizontal layout for 3-5 StatCards across an entity header.
 * Auto-grids to equal columns. Wraps to multi-row on narrow viewports.
 */
export function StatCardRow({ children, style, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      style={{
        display: 'grid',
        // `min(220px, 100%)` as the track minimum: on desktop the row still
        // auto-fits ~220px columns, but at phone width (<220px available) the
        // track collapses to 100% instead of overflowing the viewport — one
        // full-width KPI card per row rather than a horizontal-scroll spill.
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))',
        gap: 'var(--sp-4)',
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}
